import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Route, Routes, Navigate } from 'react-router-dom';
import { UserRole } from '@pe/shared';
import { AppLayout } from './components/layout/AppLayout';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import { useAuthBootstrap } from './hooks/useAuth';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { useRealtime } from './hooks/useRealtime';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoginPage } from './pages/auth/LoginPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import {
  AccessibilityPage,
  MentionsLegalesPage,
  PrivacyPolicyPage,
} from './pages/legal/LegalPage';

// Pages chargées à la demande (réduit le bundle initial)
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const TicketsListPage = lazy(() => import('./pages/tickets/TicketsListPage').then((m) => ({ default: m.TicketsListPage })));
const TicketCreatePage = lazy(() => import('./pages/tickets/TicketCreatePage').then((m) => ({ default: m.TicketCreatePage })));
const TicketDetailPage = lazy(() => import('./pages/tickets/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })));

// Sections lourdes : charts (Reports) et admin (data grids)
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const AdminHomePage = lazy(() => import('./pages/admin/AdminHomePage').then((m) => ({ default: m.AdminHomePage })));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminCategoriesPage = lazy(() => import('./pages/admin/AdminCategoriesPage').then((m) => ({ default: m.AdminCategoriesPage })));
const AdminMacrosPage = lazy(() => import('./pages/admin/AdminMacrosPage').then((m) => ({ default: m.AdminMacrosPage })));
const AdminWebhooksPage = lazy(() => import('./pages/admin/AdminWebhooksPage').then((m) => ({ default: m.AdminWebhooksPage })));
const AdminTeamsPage = lazy(() => import('./pages/admin/AdminTeamsPage').then((m) => ({ default: m.AdminTeamsPage })));
const AdminSlaPage = lazy(() => import('./pages/admin/AdminSlaPage').then((m) => ({ default: m.AdminSlaPage })));
const AdminRoutingPage = lazy(() => import('./pages/admin/AdminRoutingPage').then((m) => ({ default: m.AdminRoutingPage })));

function PageFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress aria-label="Chargement de la page" />
    </Box>
  );
}

export function App() {
  useAuthBootstrap();
  useRealtime();
  useInactivityLogout();

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/legal/mentions" element={<MentionsLegalesPage />} />
        <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/legal/accessibility" element={<AccessibilityPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/app" element={<DashboardPage />} />
            <Route path="/app/tickets" element={<TicketsListPage />} />
            <Route path="/app/tickets/new" element={<TicketCreatePage />} />
            <Route path="/app/tickets/:id" element={<TicketDetailPage />} />
            <Route path="/app/settings" element={<SettingsPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowedRoles={[UserRole.AGENT, UserRole.ADMIN]} />}>
          <Route element={<AppLayout />}>
            <Route path="/app/reports" element={<ReportsPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowedRoles={[UserRole.ADMIN]} />}>
          <Route element={<AppLayout />}>
            <Route path="/app/admin" element={<AdminHomePage />} />
            <Route path="/app/admin/users" element={<AdminUsersPage />} />
            <Route path="/app/admin/categories" element={<AdminCategoriesPage />} />
            <Route path="/app/admin/teams" element={<AdminTeamsPage />} />
            <Route path="/app/admin/sla" element={<AdminSlaPage />} />
            <Route path="/app/admin/routing" element={<AdminRoutingPage />} />
            <Route path="/app/admin/macros" element={<AdminMacrosPage />} />
            <Route path="/app/admin/webhooks" element={<AdminWebhooksPage />} />
          </Route>
        </Route>

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}
